import React, { useState, useEffect, useCallback } from "react";
import apiService from "../../../services/api";
import { useNavigate } from "react-router-dom";

const formatTemplateDate = (value) => {
  if (!value) {
    return "Just now";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const truncateContent = (content, maxLength = 150) => {
  if (!content || typeof content !== "string") {
    return "";
  }
  if (content.length <= maxLength) {
    return content;
  }
  return content.substring(0, maxLength).trim() + "...";
};

const PromptTemplateListGrid = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.listPromptTemplates({ category: 'custom' });
      if (response?.success) {
        const templatesData = response.data?.templates ?? [];
        const normalizedTemplates = templatesData.map((template) => ({
          id: template.templateId || template.id,
          name: template.name,
          version: template.version,
          content: template.content,
          isActive: Boolean(template.isActive),
          updatedAt: formatTemplateDate(
            template.changeDate || template.createDate
          ),
        }));

        setTemplates(normalizedTemplates);

        const backendTotal = Number(response.data?.totalCount);
        setTotalCount(
          Number.isFinite(backendTotal)
            ? backendTotal
            : normalizedTemplates.length
        );
      } else {
        setError(response?.message || "Failed to load prompt templates");
      }
    } catch (err) {
      console.error("Error loading prompt templates:", err);
      setError(
        err.response?.data?.message || "Failed to load prompt templates"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const templateCountLabel =
    loading && templates.length === 0
      ? "Loading..."
      : `${totalCount ?? templates.length} templates`;
  return (
    <div className="user-home-layout__card">
      <div className="user-home-layout__templates-meta">
        <span className="user-home-layout__badge user-home-layout__badge--info">
          {templateCountLabel}
        </span>
        <p className="user-home-layout__card-text user-home-layout__card-text--muted">
          Templates help you standardize prompts across teams. Select one to
          view details, edit, or duplicate.
        </p>
      </div>

      {error && (
        <p className="user-home-layout__card-text" style={{ color: "#b91c1c" }}>
          {error}
        </p>
      )}

      {loading && templates.length === 0 ? (
        <p className="user-home-layout__card-text user-home-layout__card-text--muted">
          Loading templates...
        </p>
      ) : !loading && templates.length === 0 && !error ? (
        <p className="user-home-layout__card-text user-home-layout__card-text--muted">
          No prompt templates yet. Use "Add Template" to create one.
        </p>
      ) : templates.length > 0 ? (
        <div className="user-home-layout__templates-grid">
          {templates.map((template) => (
            <article
              key={template.id}
              className="user-home-layout__template-card"
            >
              <header className="user-home-layout__template-card-header">
                <h2 className="user-home-layout__template-title">
                  {template.name}
                </h2>
                <div className="user-home-layout__template-tags">
                  <span className="user-home-layout__template-badge">
                    v{template.version}
                  </span>
                  <span
                    className={`user-home-layout__template-status ${
                      template.isActive
                        ? "user-home-layout__template-status--active"
                        : "user-home-layout__template-status--inactive"
                    }`}
                  >
                    {template.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </header>

              <p className="user-home-layout__template-content">
                {truncateContent(template.content)}
              </p>

              <footer className="user-home-layout__template-footer">
                <span className="user-home-layout__template-updated">
                  Updated {template.updatedAt}
                </span>
                <button
                  type="button"
                  className="user-home-layout__action-button user-home-layout__action-button--primary user-home-layout__template-action"
                  onClick={() =>
                    navigate(`/user-home/prompt-templates/${template.id}`)
                  }
                >
                  View
                </button>
              </footer>
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default PromptTemplateListGrid;
